import { useLatestRequest } from '@/hooks/use-latest-request'
import { PolygonType } from '@/lib/enums/PolygonType'
import { getErrorMessage } from '@/lib/utils'
import { useEffect, useEffectEvent, useRef, useState } from 'react'
import { toast } from 'sonner'
import {
  DEFAULT_SPECIALTY_LOCATION_FEE,
  MAX_SPECIALTY_LOCATIONS,
  MAX_EXCLUDED_AREAS,
  NON_DELIVERY_AREA_COLOR,
  SPECIALTY_LOCATION_COLOR,
} from '../DeliveryAreaMap/constants'
import { encodePolygonFromLatLngArray } from '../DeliveryAreaMap/utils'
import {
  countExcludedAreas,
  countSpecialPriceAreas,
  FIELD_NAME,
  normalizePlaceId,
  placeRuleTypes,
} from './delivery-area-rule-utils'
import { createPlacePolygon } from './place-rule-geometry'

const createReferenceId = (prefix) =>
  globalThis.crypto?.randomUUID?.() ??
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`

function usePlaceRuleSearch(fetchSuggestions) {
  const inputRef = useRef(null)
  const [search, setSearch] = useState('')
  const [suggestionState, setSuggestionState] = useState({ query: '', suggestions: [] })
  const { beginLatestRequest, cancelPendingRequests, isLatestRequest } = useLatestRequest()
  const fetchLatestSuggestions = useEffectEvent((input) => fetchSuggestions(input))
  const query = search.trim()
  const suggestions =
    query.length >= 3 && suggestionState.query === search ? suggestionState.suggestions : []

  useEffect(() => {
    if (query.length < 3) {
      cancelPendingRequests()
      return
    }

    const requestId = beginLatestRequest()

    fetchLatestSuggestions(search)
      .then((results) => {
        if (isLatestRequest(requestId)) {
          setSuggestionState({ query: search, suggestions: results.slice(0, 5) })
        }
      })
      .catch(() => {
        if (isLatestRequest(requestId)) {
          setSuggestionState({ query: search, suggestions: [] })
        }
      })
  }, [beginLatestRequest, cancelPendingRequests, isLatestRequest, query, search])

  const clear = () => {
    cancelPendingRequests()
    setSearch('')
    setSuggestionState({ query: '', suggestions: [] })
  }

  const focus = () => {
    requestAnimationFrame(() => inputRef.current?.focus())
  }

  return {
    clear,
    focus,
    inputRef,
    search,
    setSearch,
    suggestions,
  }
}

export function useDeliveryAreaPlaceRules({
  allDeliveryAreas,
  excludedAreas,
  fetchSuggestions,
  form,
  geometry,
  getPlaceDetails,
  isReadOnly,
  maps,
  onExistingAreaSelected,
  onPlaceRuleAdded,
  setDeliveryAreas,
  setEnableExclusions,
  setEnableSpecialtyLocations,
  specialPriceAreas,
}) {
  const specialPlaceSearch = usePlaceRuleSearch(fetchSuggestions)
  const excludedPlaceSearch = usePlaceRuleSearch(fetchSuggestions)
  const isAddingPlaceRuleRef = useRef(false)
  const [isAddingPlaceRule, setIsAddingPlaceRule] = useState(false)

  const addPlaceRule = async (suggestion, ruleType) => {
    if (isReadOnly || isAddingPlaceRuleRef.current) {
      return
    }

    const isExcluded = ruleType === placeRuleTypes.excluded
    const search = isExcluded ? excludedPlaceSearch : specialPlaceSearch
    const resetSearchInput = () => {
      search.clear()
      search.focus()
    }

    if (!isExcluded && specialPriceAreas.length >= MAX_SPECIALTY_LOCATIONS) {
      toast.error(`Maximum of ${MAX_SPECIALTY_LOCATIONS} special price places allowed`)
      resetSearchInput()
      return
    }

    if (isExcluded && excludedAreas.length >= MAX_EXCLUDED_AREAS) {
      toast.error(`Maximum of ${MAX_EXCLUDED_AREAS} no-delivery areas allowed`)
      resetSearchInput()
      return
    }

    isAddingPlaceRuleRef.current = true
    setIsAddingPlaceRule(true)

    let shouldResetSearchInput = false

    try {
      const details = await getPlaceDetails(suggestion.value)
      shouldResetSearchInput = true
      const currentAreas = form.getFieldValue(FIELD_NAME) ?? allDeliveryAreas ?? []
      const currentSpecialPriceCount = countSpecialPriceAreas(currentAreas)
      const currentExclusionCount = countExcludedAreas(currentAreas)

      if (!isExcluded && currentSpecialPriceCount >= MAX_SPECIALTY_LOCATIONS) {
        toast.error(`Maximum of ${MAX_SPECIALTY_LOCATIONS} special price places allowed`)
        return
      }

      if (isExcluded && currentExclusionCount >= MAX_EXCLUDED_AREAS) {
        toast.error(`Maximum of ${MAX_EXCLUDED_AREAS} no-delivery areas allowed`)
        return
      }

      const placeId = normalizePlaceId(
        details.placeId || suggestion.id || suggestion.value?.placeId
      )
      const existingAreaIndex = placeId
        ? currentAreas.findIndex((area) => normalizePlaceId(area.google_place_id) === placeId)
        : -1

      if (existingAreaIndex >= 0) {
        onExistingAreaSelected(existingAreaIndex)
        return
      }

      if (!geometry?.encoding || !maps) {
        toast.error('The map is still loading. Try again in a moment.')
        return
      }

      const lat = Number(details.lat)
      const lng = Number(details.lng)

      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        toast.error('Choose a place result with a map location.')
        return
      }

      const encodedPolygon = encodePolygonFromLatLngArray(
        createPlacePolygon(lat, lng),
        geometry,
        maps
      )

      if (!encodedPolygon) {
        toast.error('Unable to create a map rule for that place.')
        return
      }

      const referenceId = createReferenceId(isExcluded ? 'excluded-place' : 'special-place')
      const name = suggestion.name || details.formattedAddress || 'Special place'
      const newArea = {
        encoded_polygon: encodedPolygon,
        name,
        color: isExcluded ? NON_DELIVERY_AREA_COLOR : SPECIALTY_LOCATION_COLOR,
        polygon_type: isExcluded ? PolygonType.Excluded.Value : PolygonType.Location.Value,
        fee: isExcluded ? null : parseFloat(DEFAULT_SPECIALTY_LOCATION_FEE).toFixed(2),
        is_active: true,
        reference_id: referenceId,
        google_place_id: placeId || null,
        place_display_name: details.formattedAddress || suggestion.name || null,
        place_lat: lat,
        place_lng: lng,
      }

      onPlaceRuleAdded(currentAreas.length)
      setDeliveryAreas([...currentAreas, newArea], { touch: true })

      if (isExcluded) {
        setEnableExclusions(true)
      } else {
        setEnableSpecialtyLocations(true)
      }
    } catch (error) {
      toast.error('Could not add that map rule', {
        description: getErrorMessage(error),
      })
    } finally {
      if (shouldResetSearchInput) {
        resetSearchInput()
      }
      isAddingPlaceRuleRef.current = false
      setIsAddingPlaceRule(false)
    }
  }

  return {
    addPlaceRule,
    excludedPlaceSearch,
    isAddingPlaceRule,
    specialPlaceSearch,
  }
}
